import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addAppointmentToBlock,
  createBlockWithAppointment, // New thunk to create a block with one appointment
  fetchBlocksByClient,
} from "../../store/thunks/blocksThunk";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';

export default function AddToBlockModal({
  isOpen,
  onClose,
  clientId,
  standaloneAppointment,
}) {
  const dispatch = useDispatch();
  const [mode, setMode] = useState("existing"); // "existing" or "new"
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [position, setPosition] = useState(1);
  const [noBlocksFound, setNoBlocksFound] = useState(false);

  // State for new block form
  const [newBlockTitle, setNewBlockTitle] = useState("");
  const [newBlockDescription, setNewBlockDescription] = useState("");

  const blocks = useSelector((state) => state.blocks.clientBlocks);
  const loading = useSelector((state) => state.blocks.loading);
  const error = useSelector((state) => state.blocks.error);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (isOpen && clientId && mode === "existing") {
      dispatch(fetchBlocksByClient(clientId))
        .unwrap()
        .then((res) => {
          if (res.length === 0) {
            setNoBlocksFound(true);
          } else {
            setNoBlocksFound(false);
          }
        })
        .catch((err) => {
          if (err.message.includes("No blocks found for this client")) {
            setNoBlocksFound(true);
          }
        });
    }
  }, [isOpen, clientId, dispatch, mode]);

  if (!isOpen || !standaloneAppointment) return null;

  // Handler for adding appointment to an existing block
  const handleAddToBlock = async () => {
    if (!selectedBlock || !standaloneAppointment) return;
    try {
      await dispatch(
        addAppointmentToBlock({
          blockId: selectedBlock._id,
          appointmentId: standaloneAppointment._id,
          position,
        })
      ).unwrap();
      toast.success("Appointment added to existing block successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to add appointment to block:", error);
      toast.error("Failed to add appointment.");
    }
  };

  // Handler for creating a new block and adding the appointment
  const handleCreateNewBlock = async () => {
    if (!newBlockTitle) {
      toast.warning("Please provide a title for the new block.");
      return;
    }
    try {
      await dispatch(
        createBlockWithAppointment({
          appointmentId: standaloneAppointment._id,
          blockData: {
            title: newBlockTitle,
            description: newBlockDescription,
            client: clientId,
            createdBy: user._id,
          },
        })
      ).unwrap();
      toast.success("New block created and appointment added successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to create new block:", error);
      toast.error("Failed to create new block.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-3xl bg-white rounded-sm shadow-md relative mt-8">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition-colors">
          <XMarkIcon className="h-5 w-5" />
        </button>

        <div className="p-4 overflow-y-auto max-h-[70vh] flex flex-col">
          <h2 className="text-base font-semibold text-gray-900">
            Add Standalone Appointment to Block
          </h2>
          <p className="text-xs text-gray-600 mt-0.5">
            {mode === "existing"
              ? "Select an existing block to add this standalone appointment into."
              : "Create a new block and add this standalone appointment to it."}
          </p>

          {/* Mode Toggle */}
          <div className="flex gap-2 my-3">
            <button
              onClick={() => setMode("existing")}
              className={`rounded-sm px-3 py-1.5 text-xs font-medium transition-colors ${
                mode === "existing"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Add to Existing Block
            </button>
            <button
              onClick={() => setMode("new")}
              className={`rounded-sm px-3 py-1.5 text-xs font-medium transition-colors ${
                mode === "new"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}>
              Add to New Block
            </button>
          </div>

          {mode === "existing" ? (
            <>
              {loading && <p className="text-xs text-gray-600">Loading client blocks...</p>}
              {error && typeof error === "string" && (
                <p className="text-xs text-red-600">{error}</p>
              )}
              {error && typeof error === "object" && (
                <p className="text-xs text-red-600">
                  {error.message || "An error occurred."}
                </p>
              )}

              {noBlocksFound ? (
                <p className="mt-3 text-xs text-gray-600">
                  No blocks found for this client. Create a block first before
                  adding an appointment.
                </p>
              ) : (
                <div className="flex flex-col">
                  {/* Block & Session Selector - 2 Columns */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Block Selector */}
                    <div>
                      <label className="block mt-3 text-xs font-medium text-gray-700 mb-1">
                        Select a Block
                      </label>
                      <select
                        value={selectedBlock?._id || ""}
                        onChange={(e) =>
                          setSelectedBlock(
                            blocks.find((b) => b._id === e.target.value)
                          )
                        }
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-sm bg-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500/20">
                        <option value="">Select a Block</option>
                        {(blocks ?? []).map((block) => (
                          <option key={block._id} value={block._id}>
                            {block.title || `Block ${block._id.slice(-5)}`} -{" "}
                            {block.appointments.length} sessions
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Session Selector */}
                    {selectedBlock && (
                      <div>
                        <label className="block mt-3 text-xs font-medium text-gray-700 mb-1">
                          Select Session Position
                        </label>
                        <select
                          value={position}
                          onChange={(e) => setPosition(Number(e.target.value))}
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-sm bg-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500/20">
                          {Array.from(
                            { length: selectedBlock.appointments.length + 1 },
                            (_, i) => (
                              <option key={i} value={i}>
                                Session {i + 1}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleAddToBlock}
                    className="mt-4 rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    disabled={!selectedBlock}>
                    Add to Selected Block
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Block Title
                  </label>
                  <input
                    type="text"
                    value={newBlockTitle}
                    onChange={(e) => setNewBlockTitle(e.target.value)}
                    placeholder="Enter block title"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-sm bg-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500/20"
                  />
                </div>
                <button
                  onClick={handleCreateNewBlock}
                  className="rounded-sm bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 transition-colors w-full disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  disabled={!newBlockTitle}>
                  Create New Block & Add Appointment
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
